import classNames from 'classnames';
import { useGuidedTour } from 'hooks/useGuidedTour';
import { useSPIntl } from 'hooks/useIntl';
import { FC, memo, useCallback, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import type { FilterType, StatusFilter } from 'state/quotes/dashboard/filter/filter.model';
import type { DashboardFilter } from 'state/quotes/dashboard/model';
import type { PinnedGroup, QuoteIndexableData } from 'state/quotes/dashboard/selectors';
import type { GroupColor } from 'state/quotes/dashboard/tiles/tiles.model';

import { quickSearchFiltering } from './Dashboard.helper';
import { DashboardGrid } from './DashboardGrid';
import { Filter } from './filter/Filter';
import type { PinnedGroupColors } from './filter/Filter.helper';
import { ClientFilter } from './filter/Filter.helper';
import { BrokenTile } from './tile/reminders';
import { Tile } from './tile/Tile.connected';

interface DashboardProps {
  selectedFilter: DashboardFilter;
  quickSearchTerm?: string;
  quotes: QuoteIndexableData[];
  customGroups: PinnedGroup[];
  onDashboardClosed(): void;
  onTileClosed(quoteId: string): void;
  onFilterTypeChanged(filterType: FilterType): void;
  onByStatusFilterChanged(filter: StatusFilter): void;
  onByClientFilterChanged(clientId: string, product?: string): void;
  onByPinnedFilterChanged(pinnedGroup?: string): void;
  onCustomGroupRenamed(previousGroupName: string, newGroupName: string): void;
  onPinnedGroupColorChanged: (pinnedGroup: string, color?: GroupColor) => void;
  onQuickSearchTermChanged(term?: string): void;
  onTilesDeleted(quoteIds: string[]): void;

  className?: string;
}

type DashboardTileProps = Readonly<{
  quoteId: string;
  customGroups: PinnedGroup[];
  onTileClosed: (quoteId: string) => void;
  onDashboardClosed: () => void;
}>;

const DashboardTile: FC<DashboardTileProps> = ({ quoteId, customGroups, onTileClosed, onDashboardClosed }) => {
  const onThisTileClose = useCallback(() => onTileClosed(quoteId), [onTileClosed, quoteId]);
  const brokenTile = memo(() => <BrokenTile onClose={onThisTileClose} />);
  return (
    <ErrorBoundary FallbackComponent={brokenTile}>
      <Tile
        quoteId={quoteId}
        onDashboardClosed={onDashboardClosed}
        customGroups={customGroups}
        onTileClosed={onThisTileClose}
      />
    </ErrorBoundary>
  );
};

export const Dashboard: FC<DashboardProps> = ({
  selectedFilter,
  quickSearchTerm,
  quotes,
  customGroups,
  onDashboardClosed,
  onTileClosed,
  onFilterTypeChanged,
  onByStatusFilterChanged,
  onByClientFilterChanged,
  onByPinnedFilterChanged,
  onCustomGroupRenamed,
  onPinnedGroupColorChanged,
  onQuickSearchTermChanged,
  onTilesDeleted,
  className,
}) => {
  const { formatMessage, formatDisplayedDate } = useSPIntl();

  useGuidedTour();

  const filterClientName = useMemo<string | undefined>(() => {
    if (selectedFilter.type !== 'byClient' || !selectedFilter.client) {
      return undefined;
    } else {
      return selectedFilter.client === ClientFilter.NO_CLIENT_ID
        ? formatMessage({ id: 'dashboard.noClient' })
        : quotes.find(quote => quote.clientId === selectedFilter.client)?.clientName;
    }
  }, [formatMessage, quotes, selectedFilter]);

  const fullSearchFilteredQuotes = useMemo(
    () => quotes.filter(quote => quickSearchFiltering(quote, formatMessage, formatDisplayedDate, quickSearchTerm)),
    [formatMessage, formatDisplayedDate, quickSearchTerm, quotes],
  );

  const pinnedGroupColors = useMemo(
    () =>
      customGroups.reduce<PinnedGroupColors>((map, group) => {
        map[group.name] = group.color;
        return map;
      }, {}),
    [customGroups],
  );

  return (
    <div className={classNames('d-flex flex-column flex-lg-row', className)} data-e2e="dashboard">
      <Filter
        className="flex-grow-0 flex-shrink-0"
        selectedFilter={selectedFilter}
        quickSearchTerm={quickSearchTerm}
        quotes={fullSearchFilteredQuotes}
        pinnedGroupColors={pinnedGroupColors}
        onQuickSearchTermChanged={onQuickSearchTermChanged}
        onFilterTypeChanged={onFilterTypeChanged}
        onByStatusFilterChanged={onByStatusFilterChanged}
        onByClientFilterChanged={onByClientFilterChanged}
        onByPinnedFilterChanged={onByPinnedFilterChanged}
        onPinnedGroupRenamed={onCustomGroupRenamed}
        onPinnedGroupColorChanged={onPinnedGroupColorChanged}
        onTilesDeleted={onTilesDeleted}
      />
      <DashboardGrid
        selectedFilter={selectedFilter}
        filterClientName={filterClientName}
        quotes={fullSearchFilteredQuotes}
      >
        {quoteId => (
          <DashboardTile
            key={quoteId}
            quoteId={quoteId}
            onDashboardClosed={onDashboardClosed}
            onTileClosed={onTileClosed}
            customGroups={customGroups}
          />
        )}
      </DashboardGrid>
    </div>
  );
};

Dashboard.displayName = 'Dashboard';
